import { useState, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useDarkMode } from '../context/DarkModeContext';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff } from 'lucide-react';
import { Logo } from '../components/Logo';

export default function Register() {
  const { darkMode } = useDarkMode();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '';

  const [form, setForm] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const updateField = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setApiError('');
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.companyName.trim()) errs.companyName = 'Firma ist erforderlich';
    if (!form.contactPerson.trim()) errs.contactPerson = 'Ansprechpartner ist erforderlich';
    if (!form.email.trim()) {
      errs.email = 'E-Mail ist erforderlich';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = 'Ungültige E-Mail-Adresse';
    }
    if (!form.phone.trim()) {
      errs.phone = 'Telefonnummer ist erforderlich';
    }
    if (!form.password) {
      errs.password = 'Passwort ist erforderlich';
    } else {
      const missing: string[] = [];
      if (form.password.length < 8) missing.push('mindestens 8 Zeichen');
      if (!/[a-z]/.test(form.password)) missing.push('einen Kleinbuchstaben');
      if (!/[A-Z]/.test(form.password)) missing.push('einen Großbuchstaben');
      if (!/\d/.test(form.password)) missing.push('eine Zahl');
      if (missing.length > 0) {
        errs.password = 'Es fehlt: ' + (missing.length === 1 ? missing[0] : missing.slice(0, -1).join(', ') + ' und ' + missing[missing.length - 1]);
      }
    }
    if (form.password !== form.confirmPassword) {
      errs.confirmPassword = 'Passwörter stimmen nicht überein';
    }
    if (!acceptedTerms) {
      errs.acceptedTerms = 'Sie müssen den Nutzungsbedingungen und Datenschutzbestimmungen zustimmen';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setApiError('');
    try {
      await register({
        contactPerson: form.contactPerson,
        companyName: form.companyName,
        email: form.email,
        phone: form.phone,
        password: form.password,
      });
      if (redirectTo) localStorage.setItem('postVerifyRedirect', redirectTo);
      navigate('/verify-prompt');
    } catch (err: any) {
      setApiError(err.message || 'Registrierung fehlgeschlagen');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = (field: string) => {
    const hasError = errors[field];
    const border = hasError
      ? 'border-red-500'
      : darkMode
        ? 'border-slate-600'
        : 'border-slate-300';
    return `w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${border} ${
      darkMode ? 'bg-slate-700 text-white placeholder-slate-400' : 'bg-white text-slate-900 placeholder-slate-400'
    }`;
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center px-4 py-12 ${
        darkMode ? 'bg-slate-900' : 'bg-slate-50'
      }`}
    >
      <div
        className={`w-full max-w-md rounded-2xl border shadow-lg p-8 ${
          darkMode
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-slate-200'
        }`}
      >
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="mb-3">
            <Logo size="lg" />
          </div>
          <h1
            className={`text-2xl font-bold ${
              darkMode ? 'text-white' : 'text-slate-900'
            }`}
          >
            Registrieren
          </h1>
          <p
            className={`mt-1 text-sm ${
              darkMode ? 'text-slate-400' : 'text-slate-500'
            }`}
          >
            BAFA Beratungsbericht Generator
          </p>
        </div>

        {/* API Error */}
        {apiError && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
            {apiError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Firma */}
          <div>
            <label
              className={`block text-sm font-medium mb-1 ${
                darkMode ? 'text-slate-300' : 'text-slate-700'
              }`}
            >
              Firma <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.companyName}
              onChange={(e) => updateField('companyName', e.target.value)}
              placeholder="Musterfirma GmbH"
              autoComplete="organization"
              className={inputClass('companyName')}
            />
            {errors.companyName && (
              <p className="mt-1 text-xs text-red-500">{errors.companyName}</p>
            )}
          </div>

          {/* Ansprechpartner */}
          <div>
            <label
              className={`block text-sm font-medium mb-1 ${
                darkMode ? 'text-slate-300' : 'text-slate-700'
              }`}
            >
              Ansprechpartner <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.contactPerson}
              onChange={(e) => updateField('contactPerson', e.target.value)}
              placeholder="Max Mustermann"
              autoComplete="name"
              className={inputClass('contactPerson')}
            />
            {errors.contactPerson && (
              <p className="mt-1 text-xs text-red-500">{errors.contactPerson}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label
              className={`block text-sm font-medium mb-1 ${
                darkMode ? 'text-slate-300' : 'text-slate-700'
              }`}
            >
              E-Mail <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              placeholder="max@beispiel.de"
              autoComplete="email"
              className={inputClass('email')}
            />
            {errors.email && (
              <p className="mt-1 text-xs text-red-500">{errors.email}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label
              className={`block text-sm font-medium mb-1 ${
                darkMode ? 'text-slate-300' : 'text-slate-700'
              }`}
            >
              Telefon <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => updateField('phone', e.target.value)}
              placeholder="+49 123 456789"
              autoComplete="tel"
              className={inputClass('phone')}
            />
            {errors.phone && (
              <p className="mt-1 text-xs text-red-500">{errors.phone}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label
              className={`block text-sm font-medium mb-1 ${
                darkMode ? 'text-slate-300' : 'text-slate-700'
              }`}
            >
              Passwort <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => updateField('password', e.target.value)}
                placeholder="Mindestens 8 Zeichen"
                autoComplete="new-password"
                className={inputClass('password')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                  darkMode ? 'text-slate-400' : 'text-slate-500'
                }`}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-xs text-red-500">{errors.password}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label
              className={`block text-sm font-medium mb-1 ${
                darkMode ? 'text-slate-300' : 'text-slate-700'
              }`}
            >
              Passwort bestätigen <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.confirmPassword}
                onChange={(e) => updateField('confirmPassword', e.target.value)}
                placeholder="Passwort wiederholen"
                autoComplete="new-password"
                className={inputClass('confirmPassword')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                  darkMode ? 'text-slate-400' : 'text-slate-500'
                }`}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-1 text-xs text-red-500">
                {errors.confirmPassword}
              </p>
            )}
          </div>

          {/* Terms */}
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="terms"
              checked={acceptedTerms}
              onChange={(e) => {
                setAcceptedTerms(e.target.checked);
                setErrors((prev) => { const next = { ...prev }; delete next.acceptedTerms; return next; });
              }}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <label
              htmlFor="terms"
              className={`text-sm ${
                darkMode ? 'text-slate-300' : 'text-slate-600'
              }`}
            >
              Ich stimme den{' '}
              <Link
                to="/agb"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Nutzungsbedingungen
              </Link>{' '}
              und{' '}
              <Link
                to="/datenschutz"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Datenschutzbestimmungen
              </Link>{' '}
              zu
            </label>
          </div>
          {errors.acceptedTerms && (
            <p className="-mt-2 text-xs text-red-500">{errors.acceptedTerms}</p>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>{isSubmitting ? 'Wird registriert...' : 'Registrieren'}</span>
          </button>
        </form>

        {/* Login Link */}
        <p
          className={`mt-6 text-center text-sm ${
            darkMode ? 'text-slate-400' : 'text-slate-500'
          }`}
        >
          Bereits ein Konto?{' '}
          <Link
            to="/login"
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Anmelden
          </Link>
        </p>
      </div>
    </div>
  );
}
